// ProfileEditPage — Edit profile (name, email, bio, photo, country)
// Layout: avatar w/ change, form fields, save CTA

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserAvatar, CountryFlag } from '../../components/Brand';

export function ProfileEditPage() {
  const nav = useNavigate();
  const [data, setData] = useState({
    fullName: 'Brandon Lee',
    email: 'demo@funderpro.live',
    handle: '@brandonl',
    bio: 'Futures trader · 2 years in · Learning the markets every day',
    country: 'US',
    city: 'San Francisco',
    twitter: '@brandonl',
    discord: 'brandonl#1234',
    public: true,
    showPnl: true,
  });

  function save() { alert('Profile updated!'); nav(-1); }

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-32">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Edit profile</div>
        <button onClick={save} data-testid="save-profile" className="px-3 text-[15px] font-semibold text-tf-blue">Save</button>
      </div>

      {/* Avatar */}
      <div className="mx-4 mt-4 text-center">
        <div className="relative inline-block">
          <UserAvatar size={100} name={data.fullName} />
          <button onClick={() => alert('Change photo')} className="absolute bottom-0 right-0 flex h-8 w-8 items-center justify-center rounded-full bg-tf-blue text-white active:opacity-40">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
          </button>
        </div>
        <p className="mt-2 text-[12px] text-tf-text-3">Tap to change photo</p>
      </div>

      {/* Basic info */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Basic info</h2>
      <div className="mx-4 ios26-list bg-white">
        <Field label="Full name"  value={data.fullName}  onChange={(v) => setData({ ...data, fullName: v })} testid="edit-name" />
        <Field label="Handle"     value={data.handle}     onChange={(v) => setData({ ...data, handle: v })} sub="Letters, numbers, underscores" testid="edit-handle" />
        <Field label="Email"      value={data.email}      onChange={(v) => setData({ ...data, email: v })} type="email" testid="edit-email" />
        <Field label="Bio"        value={data.bio}        onChange={(v) => setData({ ...data, bio: v })} multiline sub="Max 160 characters" testid="edit-bio" />
        <div className="px-4 py-2.5 border-t border-tf-border">
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-tf-text-3">Country</label>
          <button onClick={() => nav('/portal/country')} className="mt-1 flex w-full items-center justify-between" data-testid="edit-country">
            <div className="flex items-center gap-2">
              <CountryFlag code={data.country} size={20} />
              <span className="text-[15px] font-medium text-tf-text-1">{data.country === 'US' ? 'United States' : data.country}</span>
            </div>
            <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </div>
        <Field label="City" value={data.city} onChange={(v) => setData({ ...data, city: v })} last testid="edit-city" />
      </div>

      {/* Social links */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Social links</h2>
      <div className="mx-4 ios26-list bg-white">
        <SocialField icon="𝕏" label="X / Twitter" value={data.twitter} onChange={(v) => setData({ ...data, twitter: v })} testid="edit-twitter" />
        <SocialField icon="🎮" label="Discord"     value={data.discord} onChange={(v) => setData({ ...data, discord: v })} last testid="edit-discord" />
      </div>

      {/* Privacy */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Privacy</h2>
      <div className="mx-4 ios26-list bg-white">
        <ToggleRow label="Public profile" sub="Show on leaderboard" value={data.public} onChange={(v) => setData({ ...data, public: v })} testid="privacy-public" />
        <ToggleRow label="Show P&L"        sub="Display on profile" value={data.showPnl} onChange={(v) => setData({ ...data, showPnl: v })} last testid="privacy-pnl" />
      </div>

      <p className="mt-3 px-4 text-center text-[10px] text-tf-text-3">Changes are visible immediately to your followers.</p>
    </div>
  );
}

function Field({ label, value, onChange, type, sub, multiline, last, testid }: { label: string; value: string; onChange: (v: string) => void; type?: string; sub?: string; multiline?: boolean; last?: boolean; testid?: string }) {
  return (
    <div className={['px-4 py-2.5', last ? '' : 'border-b border-tf-border'].join(' ')}>
      <label className="block text-[10px] font-semibold uppercase tracking-wider text-tf-text-3">{label}</label>
      {multiline ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testid} className="mt-0.5 w-full bg-transparent text-[15px] font-medium text-tf-text-1 focus:outline-none placeholder:text-tf-text-muted resize-none" />
      ) : (
        <input type={type || 'text'} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-0.5 w-full bg-transparent text-[15px] font-medium text-tf-text-1 focus:outline-none placeholder:text-tf-text-muted" />
      )}
      {sub && <div className="text-[10px] text-tf-text-3 mt-0.5">{sub}</div>}
    </div>
  );
}

function SocialField({ icon, label, value, onChange, last, testid }: { icon: string; label: string; value: string; onChange: (v: string) => void; last?: boolean; testid?: string }) {
  return (
    <div className={['flex items-center gap-3 px-4 py-2.5', last ? '' : 'border-b border-tf-border'].join(' ')}>
      <div className="flex h-7 w-7 items-center justify-center rounded-[8px] bg-tf-surface-1 text-[14px]">{icon}</div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-wider text-tf-text-3">{label}</div>
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full bg-transparent text-[15px] font-medium text-tf-text-1 focus:outline-none placeholder:text-tf-text-muted" />
      </div>
    </div>
  );
}

function ToggleRow({ label, sub, value, onChange, last, testid }: { label: string; sub: string; value: boolean; onChange: (v: boolean) => void; last?: boolean; testid?: string }) {
  return (
    <div data-testid={testid} className={['flex items-center gap-3 px-4 py-3', last ? '' : 'border-b border-tf-border'].join(' ')}>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-medium text-tf-text-1">{label}</div>
        <div className="text-[12px] text-tf-text-3">{sub}</div>
      </div>
      <button onClick={() => onChange(!value)} data-testid={`toggle-${testid}`} className={['relative h-7 w-12 rounded-full transition-colors', value ? 'bg-[#34C759]' : 'bg-[#E5E5EA]'].join(' ')}>
        <div className={['absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-md transition-all', value ? 'left-[22px]' : 'left-0.5'].join(' ')} />
      </button>
    </div>
  );
}
